/**
 * Select Component
 *
 * Reusable select/dropdown component with consistent styling.
 */

import { SelectHTMLAttributes } from 'react';

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
}

interface SelectProps<T extends string = string> extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  /** Current selected value */
  value: T;
  /** Available options */
  options: SelectOption<T>[];
  /** Change handler */
  onChange: (value: T) => void;
  /** Optional label displayed above the select */
  label?: string;
  /** Optional error message */
  error?: string;
  /** Full width mode */
  fullWidth?: boolean;
}

export default function Select<T extends string = string>({
  value,
  options,
  onChange,
  label,
  error,
  fullWidth = false,
  className = '',
  disabled,
  ...props
}: SelectProps<T>) {
  // pr-10 matches the padding @tailwindcss/forms reserves for the chevron it
  // paints on every select; anything narrower puts the arrow on the text.
  const baseClasses = 'pl-4 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-rack focus:border-rack bg-white';
  const stateClasses = error
    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300';
  const disabledClasses = disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : '';
  const widthClasses = fullWidth ? 'w-full' : '';

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label htmlFor={props.id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        disabled={disabled}
        className={`${baseClasses} ${stateClasses} ${disabledClasses} ${widthClasses} ${className}`}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}
